(ns ycweb.app.website.cart.model
  (:require
   [api.stock.leadtime :as ylt]
   [clojure.pprint :as pprint]
   [clojure.set :as set]
   [rum.core :as rum]
   [util.string :as str]
   [ycweb.app.website.cart.addresses :as addresses]
   [ycweb.app.website.tracking :as tracking]
   [ycweb.components.appcommon :as appcommon]
   [cemerick.url :as url]
   [ycweb.components.common :as cmn]
   [ycweb.components.grid :as grid]
   [ycweb.components.toaster :as toaster]
   [ycweb.util.comms :as comms]
   [ycweb.util.core :as u]))

;; --- Error State Management Functions ---

(defn set-checkout-error
  "Mark the checkout as having an error"
  [app-atom]
  (let [process-atom (cmn/process-cursor app-atom)]
    (swap! process-atom assoc :has-error? true)))

(defn clear-checkout-error
  "Clear checkout error state and any stored checkout validations"
  [app-atom]
  (let [process-atom (cmn/process-cursor app-atom)]
    (swap! process-atom dissoc :has-error? :cart-validation)))

;; --- Voucher change notices ---

(defn stash-voucher-notices!
  "Keep notices at [:voucher-notices] for the banner renderers, without
   toasting. Root path: must survive the checkout->payment navigation."
  [app-atom notices]
  (when (seq notices)
    (swap! app-atom update :voucher-notices
      (fn [existing]
        (->>
          (concat existing notices)
          (group-by :code)
          (vals)
          (mapv last))))))

(defn show-voucher-notices!
  "Stash notices for the banner renderers AND toast each one."
  [app-atom notices]
  (when (seq notices)
    (stash-voucher-notices! app-atom notices)
    (swap! app-atom update :voucher-notices-dismissed
      #(apply dissoc % (map :code notices)))
    (doseq [{:keys [msg]} notices]
      (toaster/open-toast app-atom :warning msg {:closetime 8000 :show-close-button true}))))

(defn clear-voucher-notices!
  "Drop transient notices at the start of a fresh checkout journey;
   still-relevant voids reappear via the summary."
  [app-atom]
  (swap! app-atom dissoc :voucher-notices))

(defn- stale-reduction?
  "A reduction only holds while its payment is still at the reduced amount.
   Growing the cart back resizes the payment without emitting a notice, so the
   stashed one has to retire itself."
  [payments {:keys [action code new_amount]}]
  (and
    (= action "reduced")
    (not
      (some
        #(and (= (str (:reference %)) (str code)) (= (:amount %) new_amount))
        payments))))

(defn voucher-notices
  "Reactive: summary-derived plus transient notices, minus dismissals
   ({code true}, written by the banner's close button) and superseded
   reductions."
  [app-atom]
  (let [dismissed (or (cmn/curact app-atom [:voucher-notices-dismissed]) {})

        payments
        (->>
          (cmn/curact app-atom [:cart-summary :payments])
          (filter #(= (:pmttype %) "Voucher")))]
    (->>
      (concat
        (cmn/curact app-atom [:cart-summary :voucher_notices])
        (cmn/curact app-atom [:voucher-notices]))
      (remove #(get dismissed (:code %)))
      (remove #(stale-reduction? payments %))
      (group-by :code)
      (vals)
      (mapv first))))

(defn has-checkout-error?
  "Check if checkout has an error"
  [app-atom]
  (let [process-atom (cmn/process-cursor app-atom)]
    (cmn/curact process-atom [:has-error?])))

;; Cart status helpers
(defn has-discontinued-items? [cart-summary]
  (->>
    (:items cart-summary)
    (group-by :variation_uuid)
    (map
      (fn [[vuuid items]]
        [(first items) (get (:variations cart-summary) vuuid) (count items)]))
    (filter
      (fn [[item variation qty]]
        (and
          (< (:sellablestock variation) qty)
          (= (:notavailable variation) 2))))
    (not-empty)
    (boolean)))

(defn has-staff-discount? [cart-summary]
  (->>
    (:payments cart-summary)
    (filter #(= (:pmttype %) "Staff"))
    (not-empty)
    (boolean)))

(defn has-registry-purchase-items? [cart-summary]
  (->>
    (:items cart-summary)
    (filter :forregistry_uuid)
    (not-empty)
    (boolean)))

(defn delivery-types [cart-summary]
  (->>
    (:items cart-summary)
    (remove :forregistry_uuid)
    (map :variation_uuid)
    (select-keys (:variations cart-summary))
    (vals)
    (map
      #(or (u/presence (:variation_delivery %)) "delivery"))
    (set)))

(defn has-delivery-type-items? [cart-summary delivery-type]
  (contains?
    (delivery-types cart-summary)
    delivery-type))

(defn has-physical-items? [cart-summary]
  (->
    (set/intersection
      (delivery-types cart-summary)
      #{"dropship" "dropship_collect" "delivery"})
    (empty?)
    (not)))

(defn free-special-item?
  "True for items a special gave away for free (gift/ratio free items). The
   stock classifier marks R0 SKUs \"Not Available\" (no retail price set), which
   must not block checkout or show a stock warning on a line the customer isn't
   paying for. The added+R0 fallback covers rows written before gift adds set
   the specialfreeitem flag."
  [item]
  (boolean
    (or
      (get-in item [:special :freeitem])
      (and
        (get-in item [:special :added])
        (zero? (or (:price item) 0))))))

(defn- collection-chosen? [v]
  (boolean (and (u/presence v) (not= "0" v))))

(defn missing-collection-choice? [cart-summary]
  (case (get-in cart-summary [:cart :delivery :method])
    "collect"
    (not (collection-chosen? (get-in cart-summary [:cart :delivery :collection_store_uuid])))

    "postnet"
    (not (collection-chosen? (get-in cart-summary [:cart :delivery :collection_point_uuid])))

    false))

(defn stock-blocked-validations
  "Client-side mirror of the server's validate-stock-availability, derived from
   summary data (the variation's :checkout_blocked flag is computed server-side
   with the exact validator predicate). Entries use the same shape as server
   validation maps so the checkout banner renders them identically."
  [cart-summary]
  (->>
    (:items cart-summary)
    (remove free-special-item?)
    (remove :forregistry_uuid)
    (keep
      (fn [item]
        (let [variation (get (:variations cart-summary) (:variation_uuid item))]
          (when (:checkout_blocked variation)
            {:field "stock"
             :field-path [:items (:cartitem_uuid item) :stock]
             :code "not_available"
             :message
             (str (:title variation) ": "
               (get-in variation [:stock_message :message :message]))}))))
    vec))

(defn gift-row-stock-copy
  "Stock line for a free-gift row: qty-aware pick from w = row qty,
   f = dispatch-now units, s = counted supplier units (gift_supply absent =
   uncapped supplier). No quantities in the copy. Returns {:text :style};
   nil when fully unavailable (the gift_unavailable state handles that)."
  [qty {:keys [gift_freestock gift_supply gift_stock_message]}]
  (let [w (or qty 1)
        f (max 0 (or gift_freestock 0))
        s (when (some? gift_supply) (max 0 (- gift_supply f)))
        lt (max 1 (or (:leadtime gift_stock_message) 0))
        supplier-range (ylt/range-text lt (ylt/max-days lt))
        supplier-tail (str "dispatches from us in " supplier-range ".")]
    (cond
      ;; Counted supply exhausted by this row — limited variants
      (and (some? s) (>= w (+ f s)))
      (cond
        (and (pos? f) (pos? s))
        {:style :important
         :text (str "Limited stock left. Limited stock available from supplier, " supplier-tail)}

        (pos? f)
        {:style :important :text "Limited stock left."}

        (pos? s)
        {:style :important
         :text (str "Limited stock available from supplier, " supplier-tail)}

        :else nil)

      (<= w f)
      {:style :success :text "In stock, can dispatch immediately."}

      (zero? f)
      {:style :info
       :text
       (cond
         (:message gift_stock_message) (:message gift_stock_message)
         (ylt/weeks? lt) (str "Ships from us in " supplier-range)
         :else (str "Stock available from supplier. Dispatches from us in " supplier-range "."))}

      :else
      {:style :info
       :text (str "In stock, can dispatch immediately. More available from supplier, " supplier-tail)})))

(defn payment-type-list [cart-summary pmttype]
  (->>
    (:payments cart-summary)
    (filter #(= (:pmttype %) pmttype))))

(defn dropship-only? [cart-summary]
  (let [delivery-tps
        (delivery-types cart-summary)]
    (= delivery-types
      (set/intersection delivery-tps #{"dropship" "dropship_collect"}))))

;; Helper function to roll up cart items by variation_uuid and price
(defn roll-up-cart-items [items variations]
  (let [indexed (map-indexed (fn [idx item] (assoc item :idx idx)) items)
        grouped (group-by (juxt (comp :groupnumber :special) :variation_uuid :price #(str (dissoc (:cover_voucher %) :uuid))) indexed)]
    (->>
      (for [[[groupnumber variation_uuid price vch] items] grouped]
        (let [variation (get variations variation_uuid)]
          (-> (first items)
            (assoc :item-name (or (:title variation) "Unknown item"))
            (assoc :qty (count items))
            (assoc :groupnumber groupnumber)
            (assoc :idx (reduce min (map :idx items)))
            (assoc :cartitem_uuids (mapv :cartitem_uuid items))
            (assoc :url (:page_url variation)))))
      (sort-by (juxt :groupnumber :idx)))))

(defn line-checkout-issue
  "Why a rolled-up cart line (see roll-up-cart-items) blocks checkout, or nil.
   Mirrors the checkout button's gates so a greyed-out button can always point
   at a line:
   - {:type :unavailable} the stock classifier blocks the variation
     (stock-blocked-validations), or it's discontinued with nothing left
   - {:type :reduce :available n :excess k} discontinued, and the cart holds
     k more units of the variation than the n left (has-discontinued-items?)"
  [cart-summary line]
  (let [variation (get (:variations cart-summary) (:variation_uuid line))
        sellable (or (:sellablestock variation) 0)

        ;; has-discontinued-items? counts every unit of the variation, which
        ;; can span lines (e.g. two prices), not just this line's qty
        variation-qty
        (->>
          (:items cart-summary)
          (filter #(= (:variation_uuid %) (:variation_uuid line)))
          count)]
    (cond
      (or (free-special-item? line) (:forregistry_uuid line))
      nil

      (:checkout_blocked variation)
      {:type :unavailable}

      (and (= 2 (:notavailable variation)) (< sellable variation-qty))
      (if (pos? sellable)
        {:type :reduce
         :available sellable
         :excess (- variation-qty sellable)}
        {:type :unavailable})

      :else
      nil)))

(defn lines-with-checkout-issues
  "Rolled-up cart lines that block checkout, each with its :checkout-issue."
  [cart-summary]
  (->>
    (roll-up-cart-items (:items cart-summary) (:variations cart-summary))
    (keep
      (fn [line]
        (when-let [issue (line-checkout-issue cart-summary line)]
          (assoc line :checkout-issue issue))))))

;; Debounced quantity adjustment state
(defonce qty-pending (atom {}))
(defonce qty-timer (atom nil))
(defonce removing-groups (atom #{}))

(defn clear-qty-pending []
  (reset! qty-pending {})
  (reset! removing-groups #{}))

(defn- cancel-qty-timer! []
  #?(:cljs
     (when-let [t @qty-timer]
       (js/clearTimeout t)
       (reset! qty-timer nil))))

(defn get-cart-uuid [app-atom]
  #?(:clj
     (or
       (get-in @app-atom [:cart-summary :cart :cart_uuid])
       (u/get-cookie! app-atom :yc_cart_uuid))
     :cljs
     (try
       (let [result
             (or
               (get-in @app-atom [:cart-summary :cart :cart_uuid])
               (u/get-cookie! app-atom :yc_cart_uuid)
               (js/localStorage.getItem "yc_cart_uuid"))]
         (when (and result (not= "null" result))
           result))
       (catch :default e
         (println "ERROR: Error getting cart UUID from localStorage:" e)
         nil))))

(defonce callbacks-atom (atom {}))

(defn set-cart-uuid [app-atom cart-uuid-val]
  #?(:cljs
     (try
       (cond
         cart-uuid-val
         (do
           (js/localStorage.setItem "yc_cart_uuid" cart-uuid-val)
           ;; Also set cookie if app-atom is provided
           (when app-atom
             (u/set-cookie! app-atom :yc_cart_uuid (pr-str cart-uuid-val))))

         :else
         (do
           (js/localStorage.removeItem "yc_cart_uuid")
           ;; Also remove cookie if app-atom is provided
           (when app-atom
             (u/remove-cookie! app-atom :yc_cart_uuid))))
       (catch :default e
         (println "ERROR: Error setting cart UUID in localStorage:" e)
         (println "ERROR: Error details:" (.-message e) (.-stack e))))))

(defn fetch-delivery-list [app-atom]
  (when-not (get-in @app-atom [:delivery-list])
    (comms/ajax-call :get :web.cart/delivery.list {} app-atom)))

(defmethod comms/receive :web.cart/delivery.list [app-atom e]
  (swap! app-atom assoc :delivery-list e))

(defn fetch-suggested-products [app-atom]
  (when (empty? (:suggested-products @app-atom))
    (comms/ajax-call :get :web.cart/suggested-products {} app-atom)))

(defmethod comms/receive :web.cart/suggested-products [app-atom e]
  (when (empty? (:suggested-products @app-atom))
    (swap! app-atom assoc :suggested-products (:suggested_products e))))

(defn fetch-add-to-cart-suggested-products [app-atom {:keys [limit variation-uuid page]}]
  (let [params
        (cond-> {}
          limit (assoc :limit limit)
          variation-uuid (assoc :variation_uuid variation-uuid)
          page (assoc :page page))]
    (comms/ajax-call :get :web.cart/add-to-cart-suggested-products params app-atom)))

(defmethod comms/receive :web.cart/add-to-cart-suggested-products [app-atom e]
  (let [{:keys [cart-add-response]} (:add-to-cart @app-atom)]
    (swap! app-atom assoc :add-to-cart
      {:suggested-products (:suggested_products e)
       :banner (:banner e)
       :cart-add-response cart-add-response})
    (when cart-add-response
      (grid/open-modal app-atom :cart/add-to-cart-modal
        {:cart-add-response cart-add-response
         :config {:size :add-to-cart :success? true}}))))

(defn open-add-to-cart-modal
  "Common function to open the add-to-cart modal with cart response data"
  [app-atom cart-add-response & {:keys [variation-uuid]}]
  ;; Store the cart response and fetch suggested products first
  ;; Modal opens when suggested products arrive (in comms/receive handler)
  (swap! app-atom assoc-in [:add-to-cart :cart-add-response] cart-add-response)
  (if variation-uuid
    (fetch-add-to-cart-suggested-products app-atom
      {:limit 6
       :variation-uuid variation-uuid
       :page "add-to-cart"})
    (grid/open-modal app-atom :cart/add-to-cart-modal
      {:cart-add-response cart-add-response
       :config {:size :add-to-cart :success? true}})))

(defn fetch-cart-summary [app-atom cart-uuid & [complete-fn {:keys [refresh-specials?]}]]
  (swap! callbacks-atom assoc :web.cart/summary complete-fn)
  (comms/ajax-call :get :web.cart/summary
    (cond->
      (or
        (when cart-uuid
          {:cart_uuid cart-uuid})
        #?(:clj {:active? true}
           :cljs {}))
      refresh-specials? (assoc :refresh_specials true))
    app-atom))

(defn merge-items [removed-item-uuids old-items new-items]
  (let [removed-uuids-set (set (map str removed-item-uuids))
        new-item-uuids-set (set (map :cartitem_uuid new-items))

        ;; Keep old items that are marked as :new? and didn't come back in the summary
        preserved-new-items
        (filter
          #(and (:new? %)
             (not (contains? new-item-uuids-set (:cartitem_uuid %))))
          old-items)

        ;; Remove items whose UUIDs are in the removed-items set
        filtered-new-items
        (remove #(contains? removed-uuids-set (str (:cartitem_uuid %)))
          new-items)]

    (concat preserved-new-items filtered-new-items)))

(defn fire-checkout-step-1-once!
  "Push `checkout` step 1 + `checkout_started` once per cart per tab, when the
   customer is on the cart items step with a non-empty cart. Called from the
   summary receive handler (SPA entry into the cart) and from the cart page
   mount: on a cold load the summary arrives server-rendered, so the receive
   handler never runs in the browser and the mount is the only chance to fire.
   The sessionStorage guard makes the two paths safe to overlap."
  [app-atom summary]
  (when (and (seq (:items summary))
          (:ga_tracking summary)
          (= :cart/items (:step @(cmn/process-cursor app-atom))))
    (tracking/fire-checkout-step-once!
      (get-cart-uuid app-atom)
      "step-1"
      (conj (vec (:ga_tracking summary)) {:event "checkout_started"}))))

(defmethod comms/receive :web.cart/summary [app-atom response]
  (let [response-fixed-map
        (->
          response
          (assoc
            :applystaffdiscount
            {:apply
             (has-staff-discount? response)
             :disabled false})
          (assoc-in [:cart :billing :billingtype]
            (if (or (u/presence (:company response)) (u/presence (:vatnumber response)))
              "company"
              "individual"))
          (assoc-in [:cart :billing :addresstype]
            (or (u/presence (get-in response [:cart :billing :addresstype])) "Residential"))
          (assoc-in [:cart :delivery :addresstype]
            (or (u/presence (get-in response [:cart :billing :addresstype])) "Residential")))]
    (set-cart-uuid app-atom (:cart_uuid (:cart response-fixed-map)))
    ;; [:voucher-notices] deliberately NOT cleared here — the checkout->payment
    ;; hand-off refetches the summary and the payment screen still needs them
    (swap! app-atom update-in [:cart-summary]
      (fn [summary]
        (-> response-fixed-map
          (assoc :items
            (merge-items (:removed-items summary) (:items summary) (:items response-fixed-map)))
          (assoc :request-id (:request-id summary))
          (assoc :requests (:requests summary)))))
    ;; If cart is empty, fetch suggested products
    (when (empty? (get-in response-fixed-map [:items]))
      (fetch-suggested-products app-atom))

    (fire-checkout-step-1-once! app-atom response-fixed-map)

    (clear-qty-pending)
    (toaster/close-overlay app-atom :web.cart/loading)
    (when-let [complete-fn (get @callbacks-atom :web.cart/summary)]
      (complete-fn)
      (swap! callbacks-atom dissoc :web.cart/summary))))

(defn fetch-order-summary [app-atom order-id order-hash method & [complete-fn]]
  (swap! callbacks-atom assoc :web.cart/order-summary complete-fn)
  (fetch-delivery-list app-atom)
  (comms/ajax-call :get :web.cart/order-summary
    (cond-> {:order_id order-id}
      order-hash (assoc :h order-hash)
      method (assoc :method method))
    app-atom))

(defmethod comms/receive :web.cart/order-summary [app-atom response]
  (let [response-fixed-map
        (->
          response
          (assoc
            :applystaffdiscount
            {:apply
             (has-staff-discount? response)
             :disabled false})
          (assoc-in [:cart :billing :billingtype]
            (if (or (u/presence (:company response)) (u/presence (:vatnumber response)))
              "company"
              "individual"))
          (assoc-in [:cart :billing :addresstype]
            (or (u/presence (get-in response [:cart :billing :addresstype])) "Residential"))
          (assoc-in [:cart :delivery :addresstype]
            (or (u/presence (get-in response [:cart :billing :addresstype])) "Residential")))]

    ;; Pre-select the method the URL carries (an in-flow "different payment
    ;; method" hop, or a PHP action=pay redirect); a load with no method context
    ;; defaults to Card (211339abb9 — e.g. paying the balance after eBucks)
    (swap! app-atom assoc :order-summary
      (assoc-in response-fixed-map [:cart :options :payment]
        (if-let [method (u/presence (:method (cmn/screen-params app-atom)))]
          (url/url-decode method)
          "Card")))
    (toaster/close-overlay app-atom :web.cart/loading)
    (when-let [complete-fn (get @callbacks-atom :web.cart/order-summary)]
      (complete-fn)
      (swap! callbacks-atom dissoc :web.cart/order-summary))
    (when (= :order/confirmation (:step @(cmn/process-cursor app-atom)))
      (tracking/fire-purchase-once!
        (get-in response [:order :id])
        (:ga_tracking response)))))

(defn load-cart-list [app-atom]
  (comms/ajax-call :get :web.cart/list {} app-atom))

(defmethod comms/receive :web.cart/list [app-atom response]
  (when (and (:success response) (seq (:carts response)))
           ;; If we have any carts, use the first one
    (let [first-cart (first (:carts response))
          cart-uuid-val (:cart_uuid first-cart)]
      #?(:cljs
         (try
               ;; Try a direct approach without our wrapper function
           (js/localStorage.setItem "yc_cart_uuid_direct" cart-uuid-val)
           (catch :default e
             (println "DEBUG: Direct localStorage error in load-cart-list:" e))))
             ;; Now call our wrapper
      (set-cart-uuid app-atom cart-uuid-val)
             ;; Load the cart summary with the UUID
      (fetch-delivery-list app-atom)
      (addresses/fetch-saved-addresses app-atom)
      (fetch-cart-summary app-atom cart-uuid-val))
           ;; If no carts, create a new one
    #_(create-new-cart app-atom nil)))

(defn resume-cart
  [app-atom cart-uuid p-hash]
  (comms/ajax-call :post :web.cart/resume
    {:cart_uuid cart-uuid
     :p p-hash}
    app-atom))

(defmethod comms/receive :web.cart/resume [app-atom response]

  (cond
    (not (:success response))
    (swap! app-atom assoc :page-message
      {:type :error
       :title "Invalid Link"
       :message "This cart link is invalid or has expired."})

    :else
    (do
      (set-cart-uuid app-atom (:cart_uuid response))
      (fetch-delivery-list app-atom)
      (addresses/fetch-saved-addresses app-atom)
      (fetch-cart-summary app-atom (:cart_uuid response) nil)
      (swap! app-atom assoc :page-message
        {:type :success
         :title "Cart Resumed!"
         :message "Your saved cart has been restored."}))))

(defn load-cart-data [app-atom & [complete-fn opts]]
  (println "Loading cart data")
  ;; Check for resume parameters in URL
  (let [app-page (cmn/curact app-atom [:page])
        {:keys [params]} ((:screen app-page) app-page)
        cart-uuid-param (:cart_uuid params)
        p-hash-param (:p params)]

    (swap! app-atom assoc-in [:cart-summary :loading?] true)
    (cond
      ;; Resume cart if both parameters are present
      (and (u/presence cart-uuid-param) (u/presence p-hash-param))
      (do
        (println "Attempting to resume cart with UUID:" cart-uuid-param)
        (resume-cart app-atom cart-uuid-param p-hash-param)
        (swap! app-atom update-in [:page (:screen app-page) :params] dissoc :cart_uuid :p)
        (when complete-fn (complete-fn)))

      ;; Normal cart loading
      :else
      (let [cart-uuid (get-cart-uuid app-atom)]
        (fetch-delivery-list app-atom)

        (when-not @(addresses/address-list-atom app-atom)
          (addresses/fetch-saved-addresses app-atom))
        (fetch-cart-summary app-atom cart-uuid complete-fn opts)))))

(defn cart-action-comms-send
  [app-atom method event message complete-fn
   {:keys [cart-needed?] :or {cart-needed? true}}]
  (let [cart-uuid (get-cart-uuid app-atom)
        cart-summary-atom (rum/cursor-in app-atom [:cart-summary])
        request-id (str (u/uuid))]

    (cond
      (and cart-needed? (not cart-uuid))
      (do
        (set-checkout-error app-atom)
        (toaster/open-toast app-atom :error "No cart found, please refresh your page." {:closetime 3000 :show-close-button true})
        (when complete-fn (complete-fn {:success false :msg "No cart found, please refresh your page."})))

      :else
      (do
        (swap! cart-summary-atom
          (fn [summary]
            (-> summary
              (assoc :request-id request-id)
              (assoc-in [:requests request-id]
                {:complete-fn complete-fn}))))
        (comms/ajax-call method event
          (assoc message :cart_uuid cart-uuid :request_id request-id)
          app-atom
          {:manual-error-handler? true})))))

(defn cart-action-comms-receive [app-atom response {:keys [reload?]}]
  ;; Process GA tracking events
  (when-let [ga-events (:ga_tracking response)]
    (let [checkout-event? #(= "checkout" (:event %))]
      (tracking/process-ga-events! (remove checkout-event? ga-events))
      (doseq [event (filter checkout-event? ga-events)]
        (let [step (get-in event [:ecommerce :checkout :actionField :step])]
          (tracking/fire-checkout-step-once!
            (get-cart-uuid app-atom)
            (str "step-" step)
            (cond-> [event]
              (= 2 step) (conj {:event "checkout_placeorder"})))))))

  (show-voucher-notices! app-atom (:voucher_notices response))
  ;; delta responses carry :payments only when a refresh re-sized vouchers
  (when-let [payments (:payments response)]
    (swap! app-atom assoc-in [:cart-summary :payments] payments))

  (let [cart-summary-atom (rum/cursor-in app-atom [:cart-summary])
        process-atom (cmn/process-cursor app-atom)
        {:keys [request-id requests]} @cart-summary-atom
        resolved-request-id (or (:request_id response) request-id)
        {:keys [complete-fn]} (get requests resolved-request-id)]

    (swap! cart-summary-atom update :requests dissoc resolved-request-id)
    (cond
      (not (:success response))
      (do
        (set-checkout-error app-atom)
        (toaster/open-toast app-atom :error (:msg response) {:closetime 5000 :show-close-button true})
        (when complete-fn (complete-fn response)))

      (and reload? (= request-id resolved-request-id))
      (load-cart-data app-atom
        (fn [] (when complete-fn (complete-fn response))))

      complete-fn
      (complete-fn response))))

;; Add to cart
(defn add-to-cart [app-atom product-data & [complete-fn]]
  (let [new-item
        (assoc product-data
          :cartitem_uuids
          (map (fn [_] (str (u/uuid))) (range (or (:qty product-data) 1)))
          :mobile? (boolean (get-in @app-atom [:mobile?])))]

    (when-not (= (:addsource product-data) "cart-readd")
      (swap! app-atom update-in [:cart-summary :items] concat
        (map
          (fn [cartitem_uuid]
            (assoc product-data :cartitem_uuid cartitem_uuid
              :new? true))
          (:cartitem_uuids new-item))))

    (cart-action-comms-send app-atom :post :web.cart/item.add new-item complete-fn
      {:cart-needed? false})))

(defmethod comms/receive :web.cart/item.add [app-atom response]
  (set-cart-uuid app-atom (get-in response [:cart :cart_uuid]))
  (let [cartitem-uuids (set (map :cartitem_uuid (:items response)))]
    (swap! app-atom update [:cart-summary :items]
      (fn [items]
        (map
          (fn [item]
            (if (contains? cartitem-uuids (:cartitem_uuid item))
              (dissoc item :new?)
              item))
          items))))
  (cart-action-comms-receive app-atom response {:reload? true}))

;; Remove from cart
(defn remove-from-cart [app-atom cartitem-uuids & [complete-fn]]
  (swap! app-atom update-in [:cart-summary]
    (fn [summary]
      (let [uuid-set (set (map str cartitem-uuids))
            remaining
            (remove #(contains? uuid-set (str (:cartitem_uuid %)))
              (:items summary))
            active-groups
            (->>
              remaining
              (remove #(get-in % [:special :added]))
              (keep #(get-in % [:special :groupnumber]))
              (set))
            final-items
            (remove
              (fn [item]
                (and
                  (get-in item [:special :added])
                  (get-in item [:special :groupnumber])
                  (not (contains? active-groups (get-in item [:special :groupnumber])))))
              remaining)]
        (->
          summary
          (update :removed-items set/union uuid-set)
          (assoc :items final-items)))))

  (cart-action-comms-send app-atom :post :web.cart/item.remove
    {:cartitem_uuids cartitem-uuids}
    complete-fn {}))

(defn remove-bundle-from-cart [app-atom cartitem-uuids groupnumber & [complete-fn]]
  (cancel-qty-timer!)
  (swap! removing-groups conj groupnumber)
  (cart-action-comms-send app-atom :post :web.cart/item.remove
    {:cartitem_uuids cartitem-uuids}
    complete-fn {}))

(defn- undo-remove!
  "Un-voids the exact removed rows rather than re-adding, so the line returns
   in its original position."
  [app-atom cartitem-uuids]
  (toaster/close-toast app-atom)
  (cart-action-comms-send app-atom :post :web.cart/item.unremove
    {:cartitem_uuids (vec cartitem-uuids)}
    nil {}))

(defmethod comms/receive :web.cart/item.unremove [app-atom response]
  (cart-action-comms-receive app-atom response {:reload? true}))

(defn- open-remove-undo-toast! [app-atom cartitem-uuids]
  (let [n (count cartitem-uuids)]
    (toaster/open-toast app-atom :error
      [:span
       (str n (if (= 1 n) " item" " items") " removed from cart. ")
       [:a.toaster__link
        {:href "#"
         :on-click
         (fn [e]
           #?(:cljs (.preventDefault e))
           (undo-remove! app-atom cartitem-uuids))}
        "Undo?"]]
      {:closetime 8000 :show-close-button true})))

(defmethod grid/action :cart/remove-item [app-atom {:keys [cartitem_uuids groupnumber variation_uuid]} _]
  (if groupnumber
    (remove-bundle-from-cart app-atom cartitem_uuids groupnumber)
    (do
      (cancel-qty-timer!)
      (swap! qty-pending assoc [nil variation_uuid]
        {:loading? true :delta (- (count cartitem_uuids))})
      (cart-action-comms-send app-atom :post :web.cart/item.remove
        {:cartitem_uuids cartitem_uuids}
        (fn [response]
          (when (:success response)
            (open-remove-undo-toast! app-atom cartitem_uuids)))
        {}))))

(defmethod comms/receive :web.cart/item.remove [app-atom response]
  (cond
    (:cart/reset response)
    (do
      (swap! app-atom assoc :cart-summary {})
      (set-cart-uuid app-atom nil)
      (cart-action-comms-receive app-atom response {:reload? false}))

    :else
    (cart-action-comms-receive app-atom response {:reload? true})))

(defn- flush-qty-pending [app-atom]
  (let [pending @qty-pending]
    (swap! qty-pending
      (fn [m]
        (reduce-kv
          (fn [acc k v]
            (assoc acc k (assoc v :loading? true)))
          {} m)))
    (reset! qty-timer nil)
    (doseq [[_ {:keys [delta item]}] pending]
      (cond
        (pos? delta)
        (add-to-cart app-atom (assoc item :qty delta :addsource "cart-readd"))

        (neg? delta)
        (let [uuids (vec (take-last (Math/abs delta) (:cartitem_uuids item)))]
          (cart-action-comms-send app-atom :post :web.cart/item.remove
            {:cartitem_uuids uuids}
            (fn [response]
              (when (:success response)
                (open-remove-undo-toast! app-atom uuids)))
            {}))))))

(defn adjust-qty
  [app-atom item delta]
  (let [k [(get-in item [:special :groupnumber]) (:variation_uuid item)]]
    (swap! qty-pending update k
      (fn [pending]
        {:delta (+ (or (:delta pending) 0) delta)
         :item item})))
  #?(:cljs
     (do
       (cancel-qty-timer!)
       (reset! qty-timer
         (js/setTimeout #(flush-qty-pending app-atom) 400)))))

(defn move-to-registry [app-atom cartitem-uuids registry-order-id & [complete-fn]]
  (swap! app-atom update-in [:cart-summary]
    (fn [summary]
      (->
        summary
        (update :removed-items set/union (set (map str cartitem-uuids)))
        (assoc :items
          (remove #(contains? (set (map str cartitem-uuids)) (str (:cartitem_uuid %)))
            (:items summary))))))

  (cart-action-comms-send app-atom :post :web.cart/item.move-to-registry
    {:cartitem_uuids cartitem-uuids
     :registry_order_id registry-order-id}
    complete-fn {}))

(defmethod comms/receive :web.cart/item.move-to-registry [app-atom response]
  (when (:success response)
    (swap! app-atom update-in [:cart-summary]
      (fn [summary]
        (-> summary
          (update :removed-items set/difference (:removed-items response))
          (update :items
            (fn [items]
              (remove
                #(contains? (set (map str (:removed-items response)))
                   (str (:cartitem_uuid %)))
                items)))
          (assoc :totals (:totals response))))))
  (cart-action-comms-receive app-atom response {:reload? true}))

;; Update cart delivery details
(defn update-cart-delivery-details
  "Update cart delivery details. Optionally pass checkout step info for GA tracking.
   Step should be a map with :id (step number) and :name (step keyword)."
  [app-atom & [complete-fn {:keys [step]}]]
  (let [summary (:cart-summary @app-atom)]
    (cart-action-comms-send app-atom :post :web.cart/details
      (cond->
        {:cart (:cart summary)
         :items
         (mapcat
           (fn [item]
             (map
               (fn [uuid]
                 {:cartitem_uuid uuid :variation_uuid (:variation_uuid item)})
               (or (:cartitem_uuids item) [(:cartitem_uuid item)])))
           (:items summary))}
        step (assoc :step step))
      complete-fn {})))

(defmethod comms/receive :web.cart/details [app-atom response]
  (when (:success response)
    (swap! app-atom update-in [:cart-summary]
      (fn [summary]
        (cond->
          (assoc summary :totals (:totals response))

          ;; Sent when a delivery change re-sized voucher payments
          (:payments response)
          (assoc :payments (:payments response))))))

  (when (= :refresh (:success response))
    (toaster/open-toast app-atom :error "Cart items do not match, please check the items in your cart and try again."
      {:closetime 3000 :show-close-button true}))

  (cart-action-comms-receive app-atom response
    {:reload? (=  (:success response) :refresh)}))

;; Checkout
(defn checkout [app-atom process-atom cart-summary-atom success-fn]
  (let [cart-uuid (get-cart-uuid app-atom)

        update-success-fn
        (fn [response]
          (cond
            (not (:success response))
            (swap! process-atom assoc :submitting? false)

            :else
            ;; manual-error-handler? routes a {:success false} response to the
            ;; :web.cart/checkout receive (not the generic :service/error), so its
            ;; fail-fn resets :submitting? (button re-enables / retry works) and the
            ;; validation messages surface as a toaster.
            (comms/ajax-call :post :web.cart/checkout
              {:cart_uuid cart-uuid
               :mobile? (boolean (get-in @app-atom [:mobile?]))}
              app-atom
              {:manual-error-handler? true})))]

    (cond
      (not cart-uuid)
      (swap! process-atom assoc :submitting? false)

      :else
      (do
        (swap! process-atom assoc :submitting? true :success-fn success-fn)
        (update-cart-delivery-details app-atom update-success-fn)))))

(defmethod comms/receive :web.cart/checkout [app-atom checkout-response]
  (show-voucher-notices! app-atom (:voucher_notices checkout-response))
  ;; earlier voids live only in the summary, which the post-checkout refetch
  ;; replaces — carry them over so the payment screen can still explain
  (stash-voucher-notices! app-atom
    (get-in @app-atom [:cart-summary :voucher_notices]))
  (let [process-atom (cmn/process-cursor app-atom)
        success-fn (get-in @process-atom [:success-fn])
        fail-fn
        (fn [response msg]
          (swap! process-atom assoc :submitting? false)
          (toaster/open-toast app-atom :error (or (:msg response) msg) {:closetime 5000 :show-close-button true}))]
    (cond
      (and (not (:success checkout-response)) (seq (:validations checkout-response)))
      (do
        (swap! process-atom assoc :cart-validation
          (vec (:validations checkout-response)))

        (set-checkout-error app-atom)

        (fail-fn checkout-response
          (or (:msg checkout-response)
            (str  (str/join ", " (map :message (:validations checkout-response)))))))

      (not (:success checkout-response))
      (do
        (set-checkout-error app-atom)
        (fail-fn checkout-response (or (:msg checkout-response) "Checkout failed")))

      :else
      (do
        (clear-checkout-error app-atom)
        ;; Funnel event: the SPA analogue of PHP's processPayment page
        (tracking/fire-order-event-once!
          (:order-id checkout-response) "checkout_processpayment")
        (when success-fn (success-fn checkout-response))))))

;; Add voucher
(defn add-voucher
  "The optimistic cart lines are added only once the request is going out, so a
   reCAPTCHA that never answers can't leave unsaved vouchers on screen. Callers
   redirect to the cart from complete-fn, so a timeout only replaces their
   progress toast with the error and leaves complete-fn uncalled."
  [app-atom voucher-data & [complete-fn]]
  (appcommon/recaptcha-then (get-in @app-atom [:config :captcha-site-key])
    (fn [captcha-token]
      (swap! app-atom update-in [:cart-summary :items] concat
        (map
          (fn [_]
            {:cartitem_uuid (u/uuid)
             :price (:amount voucher-data)
             :cover-voucher (:cover voucher-data)})
          (range (:qty voucher-data))))

      (let [voucher-data'
            (assoc voucher-data
              :captcha-token captcha-token
              :mobile? (boolean (get-in @app-atom [:mobile?])))]
        (cart-action-comms-send app-atom :post :web.cart/voucher.add
          voucher-data' complete-fn {:cart-needed? false})))
    (fn []
      (toaster/open-toast app-atom :error appcommon/recaptcha-failure-msg
        {:show-close-button true}))))

(defmethod comms/receive :web.cart/voucher.add [app-atom response]
  (when-let [cart-uuid (get-in response [:cart :cart_uuid])]
    (set-cart-uuid app-atom cart-uuid))
  (cart-action-comms-receive app-atom response {:reload? true}))

;; Update voucher
(defn update-voucher
  "As add-voucher: the optimistic edit is applied only once the request is
   going out, and a reCAPTCHA timeout only shows the error."
  [app-atom cartitem-uuids voucher-data & [complete-fn]]
  (let [cartitem-uuid-set (set cartitem-uuids)]
    (appcommon/recaptcha-then (get-in @app-atom [:config :captcha-site-key])
      (fn [captcha-token]
        (swap! app-atom update-in [:cart-summary :items]
          (fn [items]
            (map
              (fn [item]
                (if (contains? cartitem-uuid-set (:cartitem_uuid item))
                  (->
                    item
                    (assoc :cover-voucher (:cover voucher-data))
                    (assoc :code (:code voucher-data))
                    (assoc :amount (:amount voucher-data)))
                  item))
              items)))

        (cart-action-comms-send app-atom :post :web.cart/voucher.update
          (->
            voucher-data
            (merge
              {:cartitem_uuids cartitem-uuid-set
               :captcha-token captcha-token}))
          complete-fn {}))
      (fn []
        (toaster/open-toast app-atom :error appcommon/recaptcha-failure-msg
          {:show-close-button true})))))

(defmethod comms/receive :web.cart/voucher.update [app-atom response]
  (cart-action-comms-receive app-atom response {:reload? true}))

;; Apply voucher payment
(defn apply-voucher-payment [app-atom voucher-code amount captcha-token & [complete-fn]]
  (cart-action-comms-send app-atom :post :web.cart/voucher.apply
    {:code voucher-code :amount amount :captcha-token captcha-token}
    complete-fn {}))

(defmethod comms/receive :web.cart/voucher.apply [app-atom response]
  (cart-action-comms-receive app-atom response {:reload? true}))

;; Remove payment
(defn remove-payment [app-atom payment-uuid & [complete-fn]]
  (cart-action-comms-send app-atom :post :web.cart/payment.void {:payment_uuid payment-uuid}
    complete-fn {}))

(defmethod comms/receive :web.cart/payment.void [app-atom response]
  (cart-action-comms-receive app-atom response {:reload? true}))

;; Apply staff discount
(defn apply-staff-discount [app-atom & [complete-fn]]
  (cart-action-comms-send app-atom :post :web.cart/staff-discount {}
    complete-fn {}))

(defmethod comms/receive :web.cart/staff-discount [app-atom response]
  (when (:success response)
    (swap! app-atom assoc-in
      [:cart :staff_public_id] (:public_id response)))

  (cart-action-comms-receive app-atom response {:reload? true}))

;; --- Save Later Functions ---

(defn save-cart-later
  "Disables the continue/checkout button (process :submitting?) from the start
   of the save until it finishes. Set immediately: the guest step machinery
   clears the flag just before calling this, in the same callback, so the
   button never re-renders enabled. Checkout itself needs no reCAPTCHA, so if
   no token arrives (appcommon/recaptcha-then on-timeout) the flag is cleared
   and checkout works again; the save is not sent without one."
  [app-atom form-data complete-fn]
  (let [process-atom (cmn/process-cursor app-atom)]
    (swap! process-atom assoc :submitting? true)
    (appcommon/recaptcha-then (get-in @app-atom [:config :captcha-site-key])
      (fn [captcha-token]
        ;; Set again: the timeout may have cleared it before a late token
        (swap! process-atom assoc :submitting? true)
        (cart-action-comms-send app-atom :post :web.cart/save-later
          {:full-name (:name form-data)
           :email (:email form-data)
           :captcha-token captcha-token}
          ;; Also runs on the no-cart early return, which sends nothing
          (fn [response]
            (swap! process-atom assoc :submitting? false)
            (when complete-fn (complete-fn response)))
          {}))
      (fn []
        (swap! process-atom assoc :submitting? false)
        (toaster/open-toast app-atom :error appcommon/recaptcha-failure-msg
          {:show-close-button true})))))

(defmethod comms/receive :web.cart/save-later [app-atom response]
  (cart-action-comms-receive app-atom response {:reload? false})
  ;; Backstop for a response that doesn't resolve to its complete-fn
  (swap! (cmn/process-cursor app-atom) assoc :submitting? false)
  (when (:success response)
    (toaster/open-toast app-atom :success "Cart saved! Check your email for a link to complete your order." {:closetime 5000 :show-close-button true})))

(defn move-item-to-wishlist [app-atom cartitem-uuids variation_uuid & [complete-fn]]
  (let [cartitem-uuid-set (set cartitem-uuids)]
    (cancel-qty-timer!)
    (swap! qty-pending assoc [nil variation_uuid]
      {:loading? true :delta (- (count cartitem-uuids))})
    (cart-action-comms-send app-atom :post :web.cart/item.move-to-wishlist
      {:cartitem_uuids cartitem-uuid-set}
      complete-fn {})))

(defn remove-bundle-from-cart-wishlist [app-atom cartitem-uuids groupnumber & [complete-fn]]
  (cancel-qty-timer!)
  (swap! removing-groups conj groupnumber)
  (cart-action-comms-send app-atom :post :web.cart/item.move-to-wishlist
    {:cartitem_uuids (set cartitem-uuids)}
    complete-fn {}))

(defmethod comms/receive :web.cart/item.move-to-wishlist [app-atom response]
  (cart-action-comms-receive app-atom response {:reload? true}))

;; --- eBucks Payment Functions ---

(defn ebucks-login
  "Authenticate user with eBucks and get session token"
  [app-atom {:keys [order-uuid id-number pin]} complete-fn]
  (appcommon/recaptcha-then (get-in @app-atom [:config :captcha-site-key])
    (fn [captcha-token]
      (cart-action-comms-send app-atom :post :web.payments/ebucks.login
        {:order_uuid order-uuid
         :id_number id-number
         :pin pin
         :captcha-token captcha-token}
        complete-fn
        {:cart-needed? false}))
    ;; Clears the login form's spinner and shows the message inline
    (fn []
      (when complete-fn
        (complete-fn {:success false :msg appcommon/recaptcha-failure-msg})))))

(defmethod comms/receive :web.payments/ebucks.login [app-atom response]
  (cart-action-comms-receive app-atom response {}))

(defn ebucks-payment
  "Process eBucks payment with OTP"
  [app-atom {:keys [order-uuid payment-uuid session-token otp account amount]} complete-fn]
  (cart-action-comms-send app-atom :post :web.payments/ebucks.payment
    {:order_uuid order-uuid
     :payment_uuid payment-uuid
     :session_token session-token
     :otp otp
     :account account
     :amount amount}
    complete-fn
    {:cart-needed? false}))

(defmethod comms/receive :web.payments/ebucks.payment [app-atom response]
  ;; Update order-summary with new totals if payment was successful
  (when (and (:success response) (:totals response))
    (swap! app-atom assoc-in [:order-summary :totals] (:totals response)))
  (cart-action-comms-receive app-atom response {}))

(defn ebucks-send-otp
  "Send or resend the OTP for payment authorization"
  [app-atom {:keys [order-uuid payment-uuid session-token amount]} complete-fn]
  (cart-action-comms-send app-atom :post :web.payments/ebucks.send-otp
    {:order_uuid order-uuid
     :payment_uuid payment-uuid
     :session_token session-token
     :amount amount}
    complete-fn
    {:cart-needed? false}))

(defmethod comms/receive :web.payments/ebucks.send-otp [app-atom response]
  (cart-action-comms-receive app-atom response {}))

;; --- Discovery Miles Payment Functions ---

(defn discovery-initiate
  "Initiate Discovery Miles payment and get PayU redirect URL"
  [app-atom {:keys [order-uuid]} complete-fn]
  (cart-action-comms-send app-atom :post :web.payments/discovery.initiate
    {:order_uuid order-uuid}
    complete-fn
    {:cart-needed? false}))

(defmethod comms/receive :web.payments/discovery.initiate [app-atom response]
  (cart-action-comms-receive app-atom response {}))

(defn discovery-check-status
  "Check Discovery Miles payment status after PayU redirect"
  [app-atom {:keys [payu-reference order-uuid]} complete-fn]
  (cart-action-comms-send app-atom :post :web.payments/discovery.check-status
    {:payu_reference payu-reference
     :order_uuid order-uuid}
    complete-fn
    {:cart-needed? false}))

(defmethod comms/receive :web.payments/discovery.check-status [app-atom response]
  ;; Update order-summary with new totals if payment was processed
  (when (and (:success response) (:totals response))
    (swap! app-atom assoc-in [:order-summary :totals] (:totals response)))
  (cart-action-comms-receive app-atom response {}))

;; PayWeb3 payment functions (Card, Amex, Zapper, Snapscan)

#?(:cljs
   (defn submit-to-paygate
     [pay-request-id checksum paygate-url]
     (let [form (js/document.createElement "form")]
       (set! (.-method form) "POST")
       (set! (.-action form) paygate-url)
       (let [field1 (js/document.createElement "input")]
         (set! (.-type field1) "hidden")
         (set! (.-name field1) "PAY_REQUEST_ID")
         (set! (.-value field1) pay-request-id)
         (.appendChild form field1))
       (let [field2 (js/document.createElement "input")]
         (set! (.-type field2) "hidden")
         (set! (.-name field2) "CHECKSUM")
         (set! (.-value field2) checksum)
         (.appendChild form field2))
       (.appendChild (.-body js/document) form)
       (.submit form))))

(defn payweb3-initiate
  "Initiate PayWeb3 payment and get PayGate redirect URL"
  [app-atom {:keys [order-uuid payment-method]} complete-fn]
  (cart-action-comms-send app-atom :post :web.payments/card.initiate
    {:order_uuid order-uuid
     :payment_method payment-method}
    complete-fn
    {:cart-needed? false}))

(defmethod comms/receive :web.payments/card.initiate [app-atom response]
  (cart-action-comms-receive app-atom response {}))

;; Zapper payment functions

(defn zapper-initiate
  [app-atom {:keys [order-uuid]} complete-fn]
  (cart-action-comms-send app-atom :post :web.payments/zapper.initiate
    {:order_uuid order-uuid}
    complete-fn
    {:cart-needed? false}))

(defmethod comms/receive :web.payments/zapper.initiate [app-atom response]
  (cart-action-comms-receive app-atom response {}))

(defn zapper-check-status
  [app-atom {:keys [payment-uuid]} complete-fn]
  (cart-action-comms-send app-atom :post :web.payments/zapper.check-status
    {:payment_uuid payment-uuid}
    complete-fn
    {:cart-needed? false}))

(defmethod comms/receive :web.payments/zapper.check-status [app-atom response]
  (cart-action-comms-receive app-atom response {}))

;; SnapScan payment functions

(defn snapscan-initiate
  [app-atom {:keys [order-uuid]} complete-fn]
  (cart-action-comms-send app-atom :post :web.payments/snapscan.initiate
    {:order_uuid order-uuid}
    complete-fn
    {:cart-needed? false}))

(defmethod comms/receive :web.payments/snapscan.initiate [app-atom response]
  (cart-action-comms-receive app-atom response {}))

(defn snapscan-check-status
  [app-atom {:keys [payment-uuid check-api]} complete-fn]
  (cart-action-comms-send app-atom :post :web.payments/snapscan.check-status
    {:payment_uuid payment-uuid
     :check_api (boolean check-api)}
    complete-fn
    {:cart-needed? false}))

(defmethod comms/receive :web.payments/snapscan.check-status [app-atom response]
  (cart-action-comms-receive app-atom response {}))

(defn update-order-payment-store
  "Update the store selected for in-store payment on an order"
  [app-atom {:keys [order-id store-uuid order-hash]} complete-fn]
  (cart-action-comms-send app-atom :post :web.cart/order.payment-store
    {:order_id order-id
     :store_uuid store-uuid
     :h order-hash}
    complete-fn
    {:cart-needed? false}))

(defmethod comms/receive :web.cart/order.payment-store [app-atom response]
  (cart-action-comms-receive app-atom response {}))
